import { SearchOutlined } from "@ant-design/icons";
import { Avatar, Checkbox, Input, Modal, Skeleton, Tag } from "antd";
import { useMemo, useState } from "react";

import type { ReviewerOption } from "../../api/reviews";

interface ReviewerAssignmentModalProps {
  open: boolean;
  projectCount: number;
  reviewers: ReviewerOption[];
  loading: boolean;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (reviewerIds: string[]) => void;
}

export function ReviewerAssignmentModal({
  open,
  projectCount,
  reviewers,
  loading,
  submitting,
  onClose,
  onConfirm,
}: ReviewerAssignmentModalProps) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const visibleReviewers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("zh-CN");
    if (!term) return reviewers;
    return reviewers.filter(
      (reviewer) =>
        reviewer.name.toLocaleLowerCase("zh-CN").includes(term) ||
        reviewer.username.toLocaleLowerCase("zh-CN").includes(term),
    );
  }, [reviewers, search]);

  const close = () => {
    setSearch("");
    setSelectedIds([]);
    onClose();
  };

  return (
    <Modal
      className="reviewer-assignment-modal"
      title={
        <div className="reviewer-modal-title">
          <strong>添加审核员</strong>
          <small>为已选择的 {projectCount} 个项目分配审核人员</small>
        </div>
      }
      open={open}
      width={520}
      okText={`确认添加（${selectedIds.length}）`}
      cancelText="取消"
      okButtonProps={{ disabled: !selectedIds.length, loading: submitting }}
      onOk={() => onConfirm(selectedIds)}
      onCancel={close}
      afterClose={() => {
        setSearch("");
        setSelectedIds([]);
      }}
    >
      <Input
        className="reviewer-search"
        allowClear
        prefix={<SearchOutlined />}
        placeholder="搜索审核员姓名或用户名"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="reviewer-list-heading">
        <strong>审核员列表</strong>
        <span>已选择 {selectedIds.length} 人</span>
      </div>
      <div className="reviewer-option-list">
        {loading ? <Skeleton active paragraph={{ rows: 4 }} /> : null}
        {!loading && !visibleReviewers.length ? (
          <div className="reviewer-empty">暂无可用审核员</div>
        ) : null}
        {visibleReviewers.map((reviewer) => (
          <label className="reviewer-option" key={reviewer.id}>
            <Checkbox
              checked={selectedIds.includes(reviewer.id)}
              onChange={(event) =>
                setSelectedIds((current) =>
                  event.target.checked
                    ? [...new Set([...current, reviewer.id])]
                    : current.filter((id) => id !== reviewer.id),
                )
              }
            />
            <Avatar>{reviewer.name.slice(0, 1)}</Avatar>
            <span className="reviewer-identity">
              <strong>{reviewer.name}</strong>
              <small>@{reviewer.username}</small>
            </span>
            <Tag bordered={false} color="blue">
              审核员
            </Tag>
            <span className={reviewer.is_idle ? "reviewer-load is-idle" : "reviewer-load"}>
              {reviewer.is_idle ? "当前空闲" : `待查阅 ${reviewer.active_task_count}`}
            </span>
          </label>
        ))}
      </div>
      <p className="reviewer-modal-note">可同时为项目分配多名审核员，重复关系会自动跳过。</p>
    </Modal>
  );
}
